<template>
  <FormKit
    v-bind="bindings"
    v-model="value"
    :options="fetchSuburbsList"
    messagesClass="max-sm:ml-0"
    :innerClass="bindings.innerClass || innerClass"
    :labelClass="labelClass"
    selectIconClass="hidden"
    maxLength="100"
    @input="handleSelection"
  />
</template>

<script setup lang="ts">
import { ref, watch, withDefaults, defineProps } from 'vue';
import { useAddressLookupStore } from '@/store';
import { storeToRefs } from 'pinia';
import type { ISuburbLookupResult, IDropdownItem } from '@/interfaces';
import { toTitleCase } from '@/utils/convert';
import { SuburbLookupValueStyle } from '@/enums/suburb-lookup-types.enum';

// Define props and emitters
// ------------------------------------------------------------- //
const emit = defineEmits(['selected']);

interface Props {
  bindings: any;
  selectedSuburb: string;
  innerClass: string;
  labelClass: string;
  suburbLookupList?: ISuburbLookupResult[];
  suburbLookupValueStyle: SuburbLookupValueStyle;
}

const props = withDefaults(defineProps<Props>(), {
  suburbLookupValueStyle: SuburbLookupValueStyle.SuburbOnly,
});

// Define Store
// ------------------------------------------------------------- //
const addressLookupStore = useAddressLookupStore();

// Define Composables
// ------------------------------------------------------------- //
const { riskSuburbsData } = storeToRefs(addressLookupStore);

// Define Component state
// ------------------------------------------------------------- //
const value = ref<string>(props.selectedSuburb);

// Lifecycle hooks
// ------------------------------------------------------------- //
watch(
  () => props.selectedSuburb,
  (newValue, oldValue) => {
    if (!oldValue && newValue) {
      value.value = newValue;
    }
  }
);

function filterLocations(searchTerm: string): ISuburbLookupResult[] {
  const lowerCaseTerm = searchTerm.toLowerCase();

  if (props.suburbLookupList && props.suburbLookupList.length > 0) {
    return props.suburbLookupList.filter(
      ({ postcode, suburb }) => postcode.toString() === lowerCaseTerm || suburb.toLowerCase() === lowerCaseTerm
    );
  }

  return [];
}

async function fetchSuburbsList({ search }: { search: string }): Promise<IDropdownItem[]> {
  try {
    if (!search || search.length < 3) return [];

    if (props.suburbLookupList) {
      addressLookupStore.setInsuranceRiskSuburbsLookup(filterLocations(search));
    } else {
      await addressLookupStore.getInsuranceRiskSuburbsLookup(search);
    }

    if (props.suburbLookupValueStyle === SuburbLookupValueStyle.SuburbAndPostcode) {
      return riskSuburbsData.value.map((item) => ({
        label: `${toTitleCase(item.suburb.toLowerCase())}, ${item.postcode}`,
        value: `${item.suburb}, ${item.postcode}`,
      }));
    } else {
      return riskSuburbsData.value.map((item) => ({
        label: `${toTitleCase(item.suburb.toLowerCase())}`,
        value: item.suburb,
      }));
    }
  } catch (error) {
    console.error('Unable to fetch suburbs list: ', error);
    return [];
  }
}

function handleSelection(suburb: string) {
  emit('selected', suburb);
}
</script>
